// components/dashboard/MobileFilterPanel.jsx
import { useState, useMemo } from "react"
import { Check } from "lucide-react"
import { LOCATIONS, SENIORITY_LEVELS } from "./Constants"
import taxonomyData from "../../assets/taxonomy.json"

const FF          = "'Plus Jakarta Sans', 'Inter', sans-serif"
const BRAND       = "#18181b"
const BRAND_LIGHT = "#18181b"
const ANYWHERE_VALUE = "anywhere"
const CATEGORIES  = taxonomyData.result

// ── Small chip ────────────────────────────────────────────────
function Chip({ checked, label, onToggle, C }) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "7px 13px", borderRadius: 8,
        border: `1px solid ${checked ? (C.accentBorder || "rgba(0,0,0,0.35)") : C.borderColor}`,
        background: checked ? (C.accentBg || "rgba(0,0,0,0.06)") : "transparent",
        cursor: "pointer", transition: "all 0.12s",
        fontSize: 12, fontWeight: checked ? 600 : 400, fontFamily: FF,
        color: checked ? (C.accentText || BRAND_LIGHT) : C.textMuted,
      }}
    >
      {checked && <Check size={9} strokeWidth={2.5} color={C.accentText || BRAND_LIGHT} />}
      {label}
    </button>
  )
}

// ══════════════════════════════════════════════════════════════
// MOBILE FILTER PANEL
// ══════════════════════════════════════════════════════════════
export default function MobileFilterPanel({ open, onClose, filters, filterSetters, C }) {
  const { selectedTaxonomies, selectedLocations, selectedLevels } = filters
  const { onTaxonomyToggle, onTaxonomyClear, setSelectedLocations, setSelectedLevels, onReset } = filterSetters

  const [activeTab, setActiveTab] = useState("role")
  const [taxQuery, setTaxQuery]   = useState("")

  // Filter taxonomy by search query
  const filteredCats = useMemo(() => {
    const q = taxQuery.trim().toLowerCase()
    if (!q) return CATEGORIES
    return CATEGORIES
      .map(cat => ({
        ...cat,
        second_category: cat.second_category
          .map(sub => ({
            ...sub,
            third_category: sub.third_category.filter(item =>
              item.display_name.toLowerCase().includes(q)
            ),
          }))
          .filter(sub => sub.third_category.length > 0),
      }))
      .filter(cat => cat.second_category.length > 0)
  }, [taxQuery])

  const isTaxSelected = id => selectedTaxonomies.some(t => t.taxonomyId === id)

  const toggleLocation = value => {
    setSelectedLocations(prev => {
      if (value === ANYWHERE_VALUE) return prev.includes(ANYWHERE_VALUE) ? [] : [ANYWHERE_VALUE]
      const without = prev.filter(v => v !== ANYWHERE_VALUE)
      return without.includes(value) ? without.filter(v => v !== value) : [...without, value]
    })
  }

  const toggleLevel = key =>
    setSelectedLevels(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])

  const allLevelKeys      = SENIORITY_LEVELS.map(l => l.key)
  const allLevelsSelected = allLevelKeys.every(k => selectedLevels.includes(k))

  const activeCount  = selectedTaxonomies.length + selectedLocations.length + selectedLevels.length
  const tabCounts    = {
    role:     selectedTaxonomies.length,
    location: selectedLocations.length,
    level:    selectedLevels.length,
  }

  const tabs = [
    { key: "role",     label: "Role" },
    { key: "location", label: "Location" },
    { key: "level",    label: "Level" },
  ]

  return (
    <>
      {open && (
        <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)" }} />
      )}

      <div style={{
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 401,
        background: C.bgCard, borderRadius: 0,
        border: `1px solid ${C.borderColor}`, borderBottom: "none",
        height: "100dvh",
        transform: open ? "translateY(0)" : "translateY(100%)",
        transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1)",
        display: "flex", flexDirection: "column",
        boxShadow: "0 -8px 40px rgba(0,0,0,0.6)",
      }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px 10px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: FF, letterSpacing: "0.06em", color: C.text }}>Filters</span>
            {activeCount > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, color: C.checkFillIcon || "#0e1a14", background: C.checkFillBg || BRAND_LIGHT, borderRadius: 5, padding: "2px 7px", fontFamily: FF }}>{activeCount}</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {activeCount > 0 && (
              <button onClick={onReset} style={{ fontSize: 11, fontWeight: 600, fontFamily: FF, color: "#dc2626", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", cursor: "pointer", padding: "4px 10px", borderRadius: 7 }}>
                Clear all
              </button>
            )}
            <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: C.bgChip, border: `1px solid ${C.borderColor}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim }}>
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", borderBottom: `1px solid ${C.borderColor}`, flexShrink: 0, padding: "0 18px" }}>
          {tabs.map(tab => {
            const isActive = activeTab === tab.key
            const cnt = tabCounts[tab.key]
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  padding: "10px 0", background: "none", border: "none", cursor: "pointer",
                  borderBottom: isActive ? `2px solid ${C.accentText || BRAND_LIGHT}` : "2px solid transparent",
                  marginBottom: -1,
                  fontSize: 12, fontWeight: isActive ? 700 : 500, fontFamily: FF,
                  color: isActive ? (C.accentText || BRAND_LIGHT) : C.textDim,
                  transition: "color 0.15s",
                }}
              >
                {tab.label}
                {cnt > 0 && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: isActive ? (C.checkFillIcon || "#0e1a14") : C.textDim, background: isActive ? (C.checkFillBg || BRAND_LIGHT) : C.borderColor, borderRadius: 9999, padding: "0 5px", lineHeight: "15px" }}>
                    {cnt}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: "auto" }}>

          {/* ── ROLE tab ── */}
          {activeTab === "role" && (
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              {/* Search */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderBottom: `1px solid ${C.borderColor}`, flexShrink: 0 }}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke={C.textDim} strokeWidth="2.2" strokeLinecap="round" style={{ flexShrink: 0 }}>
                  <circle cx="7" cy="7" r="5" /><path d="M11 11l3 3" />
                </svg>
                <input
                  type="text"
                  value={taxQuery}
                  onChange={e => setTaxQuery(e.target.value)}
                  placeholder="Search roles…"
                  style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 13, fontFamily: FF, color: C.text }}
                />
                {taxQuery && (
                  <button onClick={() => setTaxQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: C.textDim, display: "flex" }}>
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
                  </button>
                )}
                {selectedTaxonomies.length > 0 && !taxQuery && (
                  <button onClick={onTaxonomyClear} style={{ fontSize: 11, fontWeight: 600, color: "#dc2626", background: "none", border: "none", cursor: "pointer", fontFamily: FF, flexShrink: 0 }}>
                    Clear
                  </button>
                )}
              </div>

              {/* Grouped role chips */}
              <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px 20px" }}>
                {filteredCats.length === 0 ? (
                  <p style={{ textAlign: "center", color: C.textDim, fontSize: 13, fontFamily: FF, paddingTop: 32 }}>No roles found</p>
                ) : (
                  filteredCats.map(cat => (
                    <div key={cat.id} style={{ marginBottom: 18 }}>
                      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.textTertiary, fontFamily: FF, margin: "0 0 8px" }}>
                        {cat.display_name}
                      </p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {cat.second_category.flatMap(sub =>
                          sub.third_category.map(item => {
                            const sel = isTaxSelected(item.id)
                            return (
                              <Chip key={item.id} checked={sel} label={item.display_name}
                                onToggle={() => onTaxonomyToggle({ taxonomyId: item.id, title: item.display_name })} C={C} />
                            )
                          })
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── LOCATION tab ── */}
          {activeTab === "location" && (
            <div style={{ padding: "16px 18px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {LOCATIONS.map(loc => (
                  <Chip key={loc.value} checked={selectedLocations.includes(loc.value)}
                    label={loc.label} onToggle={() => toggleLocation(loc.value)} C={C} />
                ))}
              </div>
            </div>
          )}

          {/* ── LEVEL tab ── */}
          {activeTab === "level" && (
            <div style={{ padding: "16px 18px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <button onClick={() => setSelectedLevels(allLevelsSelected ? [] : allLevelKeys)}
                  style={{ fontSize: 11, fontWeight: 600, color: allLevelsSelected ? (C.accentText || BRAND_LIGHT) : C.textDim, background: "none", border: "none", cursor: "pointer", fontFamily: FF }}>
                  {allLevelsSelected ? "Deselect all" : "Select all"}
                </button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {SENIORITY_LEVELS.map(lvl => (
                  <Chip key={lvl.key} checked={selectedLevels.includes(lvl.key)}
                    label={lvl.label} onToggle={() => toggleLevel(lvl.key)} C={C} />
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Apply */}
        <div style={{ padding: "12px 18px 32px", borderTop: `1px solid ${C.borderColor}`, flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{
              width: "100%", height: 46,
              background: C.checkFillBg || BRAND_LIGHT, border: "none", borderRadius: 10,
              cursor: "pointer", fontSize: 13, fontWeight: 700,
              fontFamily: FF, color: C.checkFillIcon || "#0e1a14",
              transition: "opacity 0.12s",
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            Show results{activeCount > 0 ? ` · ${activeCount} active` : ""}
          </button>
        </div>

      </div>
    </>
  )
}
